import React, { useEffect, useState } from "react";
import { PlusIcon, Trash2 } from "lucide-react";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import Button from "src/components/app/Button";
import Input from "src/components/app/Input";
import Select from "../../Select";
import Switch from "../../Switch";
import { toast } from "sonner";
import * as medicationActions from "src/redux/slices/medicationSlice";
import Dropdown from "src/components/common/Dropdown";
import { ALPA_NUMERICS } from "src/constants/regex";
import Radio from "src/components/common/Radio";

const dosageFormOptions = [
  { label: "Tablet", value: "tablet" },
  { label: "Capsule", value: "capsule" },
  { label: "Injection", value: "injection" },
  { label: "Other", value: "other" },
];

const strengthUnitOptions = [
  { value: "mg", label: "mg" },
  { value: "mcg", label: "mcg" },
  { value: "g", label: "g" },
  { value: "ml", label: "ml" },
  { value: "IU", label: "IU" },
  { value: "units", label: "units" },
  { value: "%", label: "%" },
];

const frequencyOptions = [
  { value: "daily", label: "Everyday" },
  { value: "custom", label: "Select Days" },
];

export interface AddMedicationFormContentProps {
  onSuccess: () => void;
  handleClose: () => void;
  isEdit?: boolean;
  medicationData?: any;
}

const AddMedicationFormContent: React.FC<AddMedicationFormContentProps> = ({
  onSuccess,
  handleClose,
  isEdit = false,
  medicationData,
}) => {
  const [fields, setFields] = useState({ medicineName: "" });
  const [errors, setErrors] = useState({ medicineName: "" });
  const dispatch = useAppDispatch();
  const { addMedication } = useAppSelector((state) => state.medication);
  const [addMedicationData, setAddMedicationData] = useState({
    name: "",
    dosageForm: "tablet",
    strength: "",
    strengthUnit: "mg",
    isAddReminder: false,
  });
  const [addReminderData, setAddReminderData] = useState({
    frequency: "daily",
    selectDays: [] as string[],
    startDate: "",
    endDate: "",
    doses: [{ time: "" }],
  });
  const frequencyDays = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

  useEffect(() => {
    if (isEdit && medicationData) {
      const formattedStartDate = medicationData?.reminder?.startTime
        ? new Date(medicationData.reminder.startTime).toISOString().split("T")[0]
        : "";
      const formattedEndDate = medicationData?.reminder?.endTime
        ? new Date(medicationData.reminder.endTime).toISOString().split("T")[0]
        : "";
      setFields({ medicineName: medicationData?.name });
      setAddMedicationData((prev) => ({
        ...prev,
        dosageForm: medicationData?.dosageForm,
        strength: medicationData?.strength?.split(" ")[0],
        strengthUnit: medicationData?.strength?.split(" ")[1],
        isAddReminder: medicationData?.hasReminder,
      }));
      const formattedDoses =
        medicationData?.reminder?.customFreqAttributes?.defaultTimes?.map(
          (t: string | { time: string }) =>
            typeof t === "string" ? { time: t } : t
        ) || [{ time: "" }];
      setAddReminderData((prev) => ({
        ...prev,
        frequency: medicationData?.reminder?.frequency || "daily",
        selectDays:
          medicationData?.reminder?.customFreqAttributes?.days?.map(
            (day: any) => day.day
          ) || [],
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        doses: formattedDoses,
      }));
    }
  }, [isEdit, medicationData]);

  const handleAddDose = () => {
    setAddReminderData((prev) => ({
      ...prev,
      doses: [...prev.doses, { time: "" }],
    }));
  };
  const handleTimeChange = (index: number, value: string) => {
    setAddReminderData((prev) => ({
      ...prev,
      doses: prev.doses.map((dose, i) =>
        i === index ? { ...dose, time: value } : dose
      ),
    }));
  };
  const handleDeleteDose = (index: number) => {
    setAddReminderData((prev) => ({
      ...prev,
      doses: prev.doses.filter((_, i) => i !== index),
    }));
  };
  const handleToggleDay = (day: string) => {
    setAddReminderData((prev) => ({
      ...prev,
      selectDays: prev.selectDays.includes(day)
        ? prev.selectDays.filter((d) => d !== day)
        : [...prev.selectDays, day],
    }));
  };

  const handleSaveMedication = () => {
    if (errors.medicineName !== "") {
      toast.error(errors.medicineName);
      return;
    }
    if (
      addReminderData.startDate &&
      addReminderData.endDate &&
      new Date(addReminderData.startDate) > new Date(addReminderData.endDate)
    ) {
      toast.error("Start Date must be before End Date");
      return;
    }
    const payload = {
      name: fields.medicineName,
      dosageForm: addMedicationData.dosageForm,
      strength: addMedicationData.strength + " " + addMedicationData.strengthUnit,
      isAddReminder: addMedicationData.isAddReminder,
      reminderData: addMedicationData.isAddReminder
        ? {
            startTime: addReminderData.startDate,
            endTime: addReminderData.endDate,
            frequency: addReminderData.frequency,
            customFreqAttributes: {
              days: addReminderData.selectDays.map((d) => ({ day: d })),
              defaultTimes: addReminderData.doses.map((dose) => dose.time),
            },
          }
        : null,
    };
    if (isEdit) {
      const finalPayload = {
        ...payload,
        patientMedicationId: medicationData?.patientMedicationId,
        reminderData: payload.reminderData,
      };
      new Promise((resolve, reject) => {
        dispatch(
          medicationActions.editMedicationRequest({
            payload: finalPayload,
            resolve,
            reject,
          })
        );
      }).then(() => {
        toast.success("Medication updated successfully");
        onSuccess();
        handleClose();
      });
    } else {
      new Promise((resolve, reject) => {
        dispatch(
          medicationActions.addMedicationRequest({
            payload,
            resolve,
            reject,
          })
        );
      })
        .then(() => {
          toast.success("Medication added successfully");
          onSuccess();
          handleClose();
        })
        .catch((error) => {
          console.error("Error while adding medication", error);
          toast.error("Failed to add medication");
        });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name: fieldName, value: fieldValue } = e.target;
    const newFields = { ...fields, [fieldName]: fieldValue };
    const newErrors = { ...errors };
    if (fieldName === "medicineName") {
      if (!fieldValue.trim()) newErrors.medicineName = "Medicine Name is required";
      else if (!ALPA_NUMERICS.test(fieldValue))
        newErrors.medicineName = "Only alphabets and numbers are allowed";
      else if (fieldValue.length < 3 || fieldValue.length > 100)
        newErrors.medicineName =
          "Medicine Name must be between 3 and 100 characters";
      else newErrors.medicineName = "";
    }
    setFields(newFields);
    setErrors(newErrors);
  };

  return (
    <div className="flex flex-col h-full max-h-[calc(90vh-120px)]">
      <div className="flex-1 overflow-y-auto pr-2">
        <div className="grid grid-cols-1 gap-y-3">
          <div className="col-span-1">
            <Input
              label="Medicine Name"
              name="medicineName"
              type="text"
              placeholder="Enter Medicine Name"
              fullWidth
              value={fields.medicineName}
              onChange={handleChange}
              helperText={errors.medicineName}
              error={!!errors.medicineName}
              pattern={ALPA_NUMERICS.toString()}
            />
          </div>
          <div className="col-span-1">
            <Select
              label="Type"
              name="type"
              options={dosageFormOptions}
              fullWidth
              value={addMedicationData.dosageForm}
              onChange={(e) =>
                setAddMedicationData((prev) => ({
                  ...prev,
                  dosageForm: e.target.value,
                }))
              }
            />
          </div>
          <div className="col-span-1">
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
              Strength
            </label>
            <div className="relative w-full flex items-center justify-between gap-4">
              <div className="w-full">
                <Input
                  name="strength"
                  type="number"
                  placeholder="Enter Strength"
                  value={addMedicationData.strength}
                  onChange={(e) =>
                    setAddMedicationData((prev) => ({
                      ...prev,
                      strength: e.target.value,
                    }))
                  }
                  fullWidth
                />
              </div>
              <div className="w-full">
                <Dropdown
                  name="strengthUnit"
                  options={strengthUnitOptions}
                  fullWidth
                  value={addMedicationData.strengthUnit}
                  onChange={(value) =>
                    setAddMedicationData((prev) => ({
                      ...prev,
                      strengthUnit: value as string,
                    }))
                  }
                />
              </div>
            </div>
          </div>
          <div className="col-span-1">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium text-[var(--text-secondary)]">
                Reminder
              </p>
              <Switch
                checked={addMedicationData.isAddReminder}
                onChange={(checked) =>
                  setAddMedicationData((prev) => ({
                    ...prev,
                    isAddReminder: checked,
                  }))
                }
              />
            </div>
            {addMedicationData.isAddReminder && (
              <div className="w-full p-4 min-h-10 mt-2 bg-gray-100 rounded-md">
                <div className="flex flex-col gap-4">
                  <div className="w-full">
                    <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                      Frequency
                    </label>
                    <ul className="flex items-center justify-between gap-2">
                      {frequencyOptions.map((opt) => (
                        <li key={opt.value}>
                          <button
                            type="button"
                            onClick={() =>
                              setAddReminderData((prev) => ({
                                ...prev,
                                frequency: opt.value,
                              }))
                            }
                            className={`flex items-center gap-2 w-full text-left ${
                              addReminderData.frequency === opt.value
                                ? "border-[var(--app-primary)] bg-[var(--app-primary-bg)]"
                                : "border-gray-300"
                            }`}
                          >
                            <Radio
                              checked={addReminderData.frequency === opt.value}
                              onChange={() =>
                                setAddReminderData((prev) => ({
                                  ...prev,
                                  frequency: opt.value,
                                }))
                              }
                            />
                            <p className="font-medium text-sm text-gray-500">
                              {opt.label}
                            </p>
                          </button>
                        </li>
                      ))}
                    </ul>
                    {addReminderData.frequency === "custom" && (
                      <div className="w-full mt-2">
                        <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                          Days
                        </label>
                        <div className="flex items-center gap-2 flex-wrap">
                          {frequencyDays.map((item: string) => {
                            const isSelected =
                              addReminderData.selectDays.includes(item);
                            return (
                              <button
                                key={item}
                                type="button"
                                onClick={() => handleToggleDay(item)}
                                className={`w-8 h-8 rounded-full text-sm ${
                                  isSelected
                                    ? "bg-[var(--app-primary)] text-white"
                                    : "bg-gray-200 text-[var(--text-secondary)]"
                                }`}
                              >
                                {item.charAt(0).toUpperCase()}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="w-full">
                    <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                      Duration
                    </label>
                    <div className="w-full flex items-center justify-between space-x-2">
                      <div className="flex-1 w-1/3">
                        <Input
                          label="Start Date"
                          labelClassName="text-xs mb-0.5"
                          name="startDate"
                          type="date"
                          placeholder="Enter Start Date"
                          fullWidth
                          className="text-xs"
                          value={addReminderData.startDate}
                          onChange={(e) =>
                            setAddReminderData((prev) => ({
                              ...prev,
                              startDate: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div className="flex-1 w-1/3">
                        <Input
                          label="End Date"
                          labelClassName="text-xs mb-0.5"
                          name="endDate"
                          type="date"
                          placeholder="Enter End Date"
                          fullWidth
                          className="text-xs"
                          value={addReminderData.endDate}
                          onChange={(e) =>
                            setAddReminderData((prev) => ({
                              ...prev,
                              endDate: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <div className="w-full">
                    <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                      Pick a time for dose
                    </label>
                    <div className="w-full flex flex-col gap-2">
                      {addReminderData.doses.map(
                        (doseValue: { time: string }, index: number) => (
                          <div
                            key={index}
                            className="flex items-center gap-2"
                          >
                            <div className="w-full">
                              <Input
                                name={`dose-${index}`}
                                type="time"
                                placeholder="Enter Time"
                                value={doseValue?.time}
                                step="60"
                                onChange={(e) =>
                                  handleTimeChange(index, e.target.value)
                                }
                                fullWidth
                              />
                            </div>
                            {addReminderData.doses.length > 1 && (
                              <button
                                type="button"
                                onClick={() => handleDeleteDose(index)}
                                className="p-2 rounded-md hover:bg-red-100"
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </button>
                            )}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                  <div className="col-span-1">
                    <Button
                      variant="outlined"
                      size="sm"
                      onClick={handleAddDose}
                    >
                      <PlusIcon className="w-4 h-4" /> Add Dose
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex-shrink-0 pt-4 border-t border-gray-200 mt-4">
        <div className="relative w-full flex items-center justify-between gap-2">
          <div className="w-full">
            <Button
              variant="outlined"
              rounded="full"
              fullWidth
              onClick={handleClose}
              loading={addMedication?.isLoading}
              disabled={addMedication?.isLoading}
            >
              Cancel
            </Button>
          </div>
          <div className="w-full">
            <Button
              variant="contained"
              rounded="full"
              fullWidth
              onClick={handleSaveMedication}
              loading={addMedication?.isLoading}
              disabled={addMedication?.isLoading}
            >
              {isEdit ? "Update" : "Save"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddMedicationFormContent;
